import { useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useNavigate, useSearchParams } from 'libs/router'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import {
	ActionsSelect,
	CardWrapper,
	ConfirmModal,
	CtaLink,
	DeleteConfirmModal,
	EmptyTable,
	FormatModal,
	Header,
	SortSelect
} from 'shared/components'
import { useFilter, useSearch, useTableSelect } from 'shared/hooks'
import { routes } from 'config/routes'
import { IOption } from 'interfaces/select.interface'
import { useFetchUsers } from './queries/useFetchUsers'
import { useModals } from './hooks/useModals'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useSortingFilters } from './hooks/useSortingFilters'
import { useDeleteUserMutation } from './mutations/useDeleteUserMutation'
import { useExportUsersMutation } from './mutations/useExportUsersMutation'
import { usePayoutReminderEmailBulkMutation } from './mutations/usePayoutReminderEmailBulkMutation'
import { useRequestBulkPayoutMutation } from './mutations/useRequestBulkPayoutMutation'
import { useRequestPayoutMutation } from './mutations/useRequestPayoutMutation'
import { FiltersSection } from './organisms/FiltersSection/FiltersSection'
import { TableSection } from './organisms/TableSection'

const UsersPage = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const { handleSearch } = useSearch({ name: 'search' })
	const { handleFilter } = useFilter()
	const [checkedUsers, setCheckedUsers] = useState<number[]>([])
	const [idToBeRemoved, setIdToBeRemoved] = useState<number | null>(null)
	const [payoutUserId, setPayoutUserId] = useState<number | null>(null)
	const [chosenExportFormat, setChosenExportFormat] = useState<number | string | null>(null)
	const { users, isUsersLoading } = useFetchUsers()
	const { select } = useTableSelect({ nodes: users?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const clearSelection = () => select.fns.onRemoveAll()
	const sortingFilters = useSortingFilters()
	const [actionsFilter] = useActionFilters(!checkedUsers.length)

	const {
		isDeleteModalOpen,
		openDeleteModal,
		closeDeleteModal,
		isExportModalOpen,
		openExportModal,
		closeExportModal,
		isBulkPayoutModalOpen,
		openBulkPayoutModal,
		closeBulkPayoutModal,
		isFormatModalOpen,
		openFormatModal,
		closeFormatModal,
		isPayoutModalOpen,
		openPayoutModalOpen,
		closePayoutModal,
		isBulkPayoutReminderEmailOpen,
		openBulkPayoutReminderEmailModal,
		closeBulkPayoutReminderEmailModal
	} = useModals()

	function onSelectChange(action: any, state: any) {
		setCheckedUsers(state.ids)
	}

	const handleDelete = (id: number) => {
		setIdToBeRemoved(id)
		openDeleteModal()
	}

	const handlePayoutRequest = (id: number) => {
		setPayoutUserId(id)
		openPayoutModalOpen()
	}

	const menuOptions = useContextMenuOptions({
		handleEdit: (id) => navigate(routes.userEdit(String(id))),
		handleSinglePayoutRequest: handlePayoutRequest,
		handleDeleteModalOpen: handleDelete
	})

	const handleAction = (action: string) => {
		switch (action) {
			case 'payout':
				openBulkPayoutModal()
				break
			case 'emailPayoutReminder':
				openBulkPayoutReminderEmailModal()
				break
			case 'export':
				openExportModal()
				break
		}
	}

	const handleExportModal = () => {
		closeExportModal()
		openFormatModal()
	}

	const handleFormatChange = (data: IOption) => {
		data?.value?.toString() ? setChosenExportFormat(data.value) : setChosenExportFormat(null)
	}

	const { deleteUserMutation } = useDeleteUserMutation({
		idToBeRemoved,
		setIdToBeRemoved,
		closeDeleteModal
	})

	const { payoutReminderEmailBulkMutation } = usePayoutReminderEmailBulkMutation({
		checkedUsers,
		closeBulkPayoutReminderEmailModal
	})

	const { requestBulkPayoutMutation } = useRequestBulkPayoutMutation({
		checkedUsers,
		clearSelection,
		closeBulkPayoutModal
	})

	const { requestPayoutMutation } = useRequestPayoutMutation({
		userId: payoutUserId,
		closePayoutModal
	})

	const { exportMutation, isExportLoading } = useExportUsersMutation({
		chosenExportFormat,
		checkedUsers,
		clearSelection,
		closeFormatModal,
		setChosenExportFormat
	})

	const renderContent = () => {
		if (isUsersLoading) return <EmptyTable isLoading />
		if (!users?.data?.results?.length)
			return <EmptyTable text={searchParams.toString() ? t('No results') : t('No users yet')} />

		return <TableSection users={users} select={select} menuOptions={menuOptions} />
	}

	return (
		<>
			<Header
				title={t('Users')}
				eyebrow={t('Directory')}
				actions={
					<>
						<SortSelect {...sortingFilters} onChange={handleFilter} />
						<CtaLink to={routes.userAdd} text={t('Add user')} compact />
					</>
				}
			/>
			<div className="mx-auto max-w-[1536px] px-5 lg:flex lg:max-h-[calc(100vh-72px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<FiltersSection
						actions={
							<ActionsSelect
								variant="filters"
								options={actionsFilter.options}
								onChange={handleAction}
							/>
						}
						handleSearch={handleSearch}
						handleFilter={handleFilter}
					/>
					{renderContent()}
				</CardWrapper>
			</div>

			{isDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete')}
					isOpened={isDeleteModalOpen}
					closeModal={closeDeleteModal}
					confirm={() => deleteUserMutation()}>
					<p>{t('You are about to remove a user. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isExportModalOpen && (
				<ConfirmModal
					title={t('Export users')}
					isOpened={isExportModalOpen}
					closeModal={closeExportModal}
					confirm={handleExportModal}>
					<p>
						{t(
							'You are about to request export for multiple users. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isBulkPayoutReminderEmailOpen && (
				<ConfirmModal
					title={t('Send payment method reminder')}
					isOpened={isBulkPayoutReminderEmailOpen}
					closeModal={closeBulkPayoutReminderEmailModal}
					confirm={() => payoutReminderEmailBulkMutation()}>
					<p>
						{t(
							'You are about to send a reminder to {{ count }} users. Are you sure you want to continue?',
							{ count: checkedUsers.length }
						)}
					</p>
				</ConfirmModal>
			)}
			{isBulkPayoutModalOpen && (
				<ConfirmModal
					title={t('Request payout')}
					isOpened={isBulkPayoutModalOpen}
					closeModal={closeBulkPayoutModal}
					confirm={() => requestBulkPayoutMutation()}>
					<p>
						{t(
							'You are about to request payouts for {{ count }} users. Are you sure you want to proceed?',
							{ count: checkedUsers.length }
						)}
					</p>
				</ConfirmModal>
			)}
			{isPayoutModalOpen && (
				<ConfirmModal
					title={t('Request payout')}
					isOpened={isPayoutModalOpen}
					closeModal={closePayoutModal}
					confirm={() => requestPayoutMutation()}>
					<p>
						{t('You are about to request payouts for the user. Are you sure you want to proceed?')}
					</p>
				</ConfirmModal>
			)}
			{isFormatModalOpen && (
				<FormatModal
					disableButton={chosenExportFormat === null}
					isOpened={isFormatModalOpen}
					closeModal={closeFormatModal}
					confirm={() => exportMutation()}
					isLoading={isExportLoading}
					onChange={handleFormatChange}
				/>
			)}
		</>
	)
}

export default UsersPage
